import {
  Component,
  forwardRef,
  Input,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import {
  AgpFormUtil,
  CvaFormGroupComponent,
  ResetOptions
} from '@liberty-financial/agp-shared';
import {
  ageMaxValidator,
  dateBeforeValidator,
  dateValidator,
  GuidUtil
} from '@liberty-financial/ng-core';

import { ContactSummary, Dependent } from '@features/contacts/models';
import { AGE_DEPENDENT_MAXIMUM } from '@shared/constants';
import { IdObjectValue } from '@shared/models/schema-property-definition/id-value/id-object-value';
import { StandardSchemaUtil } from '@shared/utilities/standard-schema-util';

interface DependentForm extends IdObjectValue.Crud.Updatable {
  birthDate: string | null;
  financiallyDependent: boolean;
  name: string;
  otherGuardian: ContactSummary | null;
}

@Component({
  selector: 'spark-contacts-individual-dependents',
  templateUrl: './dependents.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IndividualDependentsComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => IndividualDependentsComponent),
      multi: true
    }
  ]
})
export class IndividualDependentsComponent
  extends CvaFormGroupComponent<Dependent.Model[], DependentForm[]>
  implements OnChanges
{
  public readonly maxDependentAge: number = AGE_DEPENDENT_MAXIMUM;
  @Input() schema!: Dependent.SchemaPropertyDefinition;
  @Input() contacts: ContactSummary[] = [];
  @Input() isRequired = false;

  deleted: { [index: number]: boolean } = {};
  expandedIndex = -1;
  form = this.fb.nonNullable.array<
    FormGroup<{
      id: FormControl<string>;
      created: FormControl<boolean>;
      deleted: FormControl<boolean>;
      birthDate: FormControl<string | null>;
      financiallyDependent: FormControl<boolean | null>;
      name: FormControl<string>;
      otherGuardian: FormControl<ContactSummary | null>;
    }>
  >([]);
  formGroup = this.fb.group({
    dependents: this.form
  });

  constructor(private readonly fb: FormBuilder) {
    super();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.isRequired) {
      AgpFormUtil.Validators.updateRequiredIfNecessary(this.form, changes.isRequired.currentValue);
    }
  }

  protected reset(opts?: ResetOptions | undefined): void {
    this.form?.reset(undefined, opts);
  }

  protected transformToForm = (
    dependents: Dependent.Model[]
  ): DependentForm[] => {
    const result: DependentForm[] = [];
    dependents.forEach((d) => {
      const contact = this.contacts.find((c) => c.id === d?.otherGuardian);
      this.add(false);
      result.push({
        ...d,
        otherGuardian: contact || null
      } as DependentForm);
    });
    return result;
  };
  protected transformToModel = (
    dependents: DependentForm[]
  ): Dependent.Model[] => {
    const result: Dependent.Model[] = [];
    dependents.forEach((d) => {
      result.push({
        ...d,
        otherGuardian: d.otherGuardian?.id
      } as Dependent.Model);
    });
    return result;
  };

  add(newItem: boolean = true): void {
    const newItemForm = this.fb.group({
      id: this.fb.nonNullable.control(GuidUtil.generate()),
      created: this.fb.nonNullable.control(newItem),
      deleted: this.fb.nonNullable.control(false),
      birthDate: [
        null as string | null,
        [
          dateValidator(),
          dateBeforeValidator(),
          ageMaxValidator(AGE_DEPENDENT_MAXIMUM)
        ]
      ],
      financiallyDependent: [null as boolean | null],
      name: this.fb.nonNullable.control(''),
      otherGuardian: [null as ContactSummary | null]
    });

    StandardSchemaUtil.attachFormGroupToSchema(newItemForm, this.schema);
    this.form.push(newItemForm);
    this.expandedIndex = this.form.length - 1;
  }

  remove(i: number): void {
    this.deleted[i] = true;
    this.form.at(i).controls.deleted.setValue(true);
  }
}
